import { useQuery } from '@apollo/client';
import moment from 'moment';
import React from 'react';
import * as styles from './TeamUpgradeInfo.module.scss';
import TrialProgressCircle from './TrialProgressCircle';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { MenuSeparator, MenuSimpleItem } from '../components/Menu';
import { gql } from '../graphql/generated/gql';
import { BillingSummaryStatus, PlanTier } from '../graphql/generated/graphql';
import { openPricingModal } from '../utils/url/pricingPath';

const TEAM_UPGRADE_INFO_QUERY = gql(/** @complexity 6 */ `
  query teamUpgradeInfo {
    currentSession {
      organization {
        id
        billingStatus
        planTier
        insertedAt
        trialEndAt
      }
    }
    # eslint-disable-next-line @graphql-eslint/require-selections
    billingSummary {
      seatCount
      seatLimit
    }
  }
`);

const TeamUpgradeInfo = () => {
  const dispatch = useAppDispatch();

  const { data } = useQuery(TEAM_UPGRADE_INFO_QUERY);

  const organization = data?.currentSession?.organization;

  if (!data || !organization) {
    return null;
  }

  const { billingSummary } = data;

  const today = moment().startOf('day');
  const trialEndDay = moment(organization.trialEndAt).startOf('day');
  const insertDay = moment(organization.insertedAt).startOf('day');
  const remainingDays = trialEndDay.diff(today, 'days');
  const trialLength = trialEndDay.diff(insertDay, 'days');

  const remainingSeats = Math.max(
    (billingSummary.seatLimit ?? 0) - (billingSummary.seatCount ?? 0),
    0,
  );

  return (
    <>
      {organization.billingStatus === BillingSummaryStatus.Trialing &&
        !!organization.trialEndAt && (
          <>
            <MenuSeparator fullWidth />
            <MenuSimpleItem>
              <div className={styles.trialInfo}>
                {remainingDays > 0 ? (
                  <>
                    <div className={styles.progressContainer}>
                      <TrialProgressCircle
                        remaining={remainingDays}
                        total={trialLength}
                      />
                    </div>
                    <div>
                      Your team is on a trial with {remainingDays} days left.{' '}
                      <Button
                        className={styles.upgradeButton}
                        variant="link"
                        onClick={() => openPricingModal(dispatch)}
                      >
                        Upgrade now
                      </Button>
                    </div>
                  </>
                ) : (
                  <div>
                    Your trial has expired. Upgrade to enable editing.{' '}
                    <Button
                      className={styles.upgradeButton}
                      variant="link"
                      onClick={() => openPricingModal(dispatch)}
                    >
                      Upgrade now
                    </Button>
                  </div>
                )}
              </div>
            </MenuSimpleItem>
          </>
        )}

      {organization.planTier === PlanTier.Free && (
        <>
          <MenuSeparator fullWidth />
          <MenuSimpleItem>
            <div className={styles.trialInfo}>
              <div className={styles.progressContainer}>
                <TrialProgressCircle
                  remaining={remainingSeats}
                  total={data.billingSummary.seatLimit ?? 0}
                />
              </div>
              <div>
                Your team is on the free plan with {remainingSeats} invites
                left.{' '}
                <Button
                  className={styles.upgradeButton}
                  variant="link"
                  onClick={() => openPricingModal(dispatch, 'users')}
                >
                  Upgrade now
                </Button>
              </div>
            </div>
          </MenuSimpleItem>
        </>
      )}
    </>
  );
};

export default TeamUpgradeInfo;
